import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import { 
  Radio, 
  Signal, 
  Wifi, 
  Bluetooth, 
  Satellite,
  CheckCircle,
  ArrowRight,
  Building2,
  Droplets,
  Truck,
  Heart,
  Home,
  Factory,
  Calendar,
  Layers,
  Battery,
  Shield,
  Globe,
  Target,
  Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/LanguageContext';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/sections/ContactForm';
import { HreflangTags } from '@/components/HreflangTags';

const ConnectivityPage = () => {
  const { t } = useLanguage();
  const location = useLocation();

  // Handle scroll to anchor on page load
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>{t('connectivityPage.metaTitle')}</title>
        <meta name="description" content={t('connectivityPage.metaDesc')} />
        <meta name="keywords" content="IoT connectivity, LoRaWAN infrastructure, Sigfox connectivity, NB-IoT network, BLE IoT tracking, satellite IoT connectivity, multi-network IoT architecture, IoT infrastructure Baltics" />
        <link rel="canonical" href="https://0gbaltics.com/connectivity" />
        <meta property="og:title" content={t('connectivityPage.metaTitle')} />
        <meta property="og:description" content={t('connectivityPage.metaDesc')} />
        <meta property="og:type" content="website" />
      </Helmet>
      <HreflangTags />

      <Header />
      
      <main className="pt-20">
        {/* SECTION 1 - HERO */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="connectivity-hero">
          <div className="container-0g">
            <div className="max-w-4xl">
              <p className="text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">{t('connectivityPage.heroBadge')}</p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                {t('connectivityPage.heroTitle')}
              </h1>
              <p className="text-xl md:text-2xl text-slate-300 mb-8 leading-relaxed">
                {t('connectivityPage.heroSubtitle')}
              </p>
              <p className="text-lg text-slate-400 leading-relaxed">
                {t('connectivityPage.heroDesc')}
              </p>
              
              {/* Technology icons */}
              <div className="flex flex-wrap gap-4 mt-12">
                {[
                  { icon: Radio, name: 'LoRaWAN' },
                  { icon: Signal, name: 'Sigfox' },
                  { icon: Wifi, name: 'NB-IoT' },
                  { icon: Bluetooth, name: 'BLE' },
                  { icon: Satellite, name: 'Satellite' },
                ].map((tech, index) => {
                  const Icon = tech.icon;
                  return (
                    <div key={index} className="flex items-center space-x-2 bg-slate-800 border border-slate-700 px-4 py-2 rounded-sm">
                      <Icon className="h-5 w-5 text-teal-400" />
                      <span className="text-white font-medium">{tech.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2 - MULTI CONNECTIVITY PHILOSOPHY */}
        <section className="py-20 md:py-32 bg-white" data-testid="multi-connectivity">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <p className="section-label">{t('connectivityPage.multiBadge')}</p>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('connectivityPage.multiTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                  {t('connectivityPage.multiDesc')}
                </p>
                
                <div className="space-y-4 mb-8">
                  {[
                    t('connectivityPage.multiPoint1'),
                    t('connectivityPage.multiPoint2'),
                    t('connectivityPage.multiPoint3'),
                    t('connectivityPage.multiPoint4'),
                    t('connectivityPage.multiPoint5'),
                  ].map((point, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 text-teal-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 font-medium">{point}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-slate-50 border border-slate-200 p-8 rounded-sm">
                <div className="flex items-center space-x-3 mb-6">
                  <Layers className="h-8 w-8 text-teal-600" />
                  <h3 className="text-xl font-semibold text-slate-900">{t('connectivityPage.commitmentTitle')}</h3>
                </div>
                <p className="text-slate-700 leading-relaxed">
                  {t('connectivityPage.commitmentDesc')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 3 - LORAWAN */}
        <section id="lorawan" className="py-20 md:py-32 bg-slate-900" data-testid="lorawan-section">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 rounded-sm bg-teal-600 flex items-center justify-center">
                    <Radio className="h-6 w-6 text-white" />
                  </div>
                  <p className="text-xs font-bold uppercase tracking-widest text-teal-400">{t('connectivityPage.lorawanBadge')}</p>
                </div>
                <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                  {t('connectivityPage.lorawanTitle')}
                </h2>
                <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                  {t('connectivityPage.lorawanDesc')}
                </p>
                
                <h3 className="text-xl font-semibold text-white mb-4">{t('connectivityPage.lorawanAdvantages')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
                  {[
                    t('connectivityPage.lorawanAdv1'),
                    t('connectivityPage.lorawanAdv2'),
                    t('connectivityPage.lorawanAdv3'),
                    t('connectivityPage.lorawanAdv4'),
                    t('connectivityPage.lorawanAdv5'),
                  ].map((advantage, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <CheckCircle className="h-5 w-5 text-teal-400 flex-shrink-0" />
                      <span className="text-slate-300 text-sm">{advantage}</span>
                    </div>
                  ))}
                </div>
                
                <p className="text-slate-400 text-sm">
                  {t('connectivityPage.lorawanNote')}
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-4">{t('connectivityPage.lorawanUseCases')}</h3>
                <div className="space-y-3">
                  {[
                    { title: t('connectivityPage.lorawanUse1Title'), desc: t('connectivityPage.lorawanUse1Desc') },
                    { title: t('connectivityPage.lorawanUse2Title'), desc: t('connectivityPage.lorawanUse2Desc') },
                    { title: t('connectivityPage.lorawanUse3Title'), desc: t('connectivityPage.lorawanUse3Desc') },
                    { title: t('connectivityPage.lorawanUse4Title'), desc: t('connectivityPage.lorawanUse4Desc') },
                    { title: t('connectivityPage.lorawanUse5Title'), desc: t('connectivityPage.lorawanUse5Desc') },
                  ].map((useCase, index) => (
                    <div key={index} className="bg-slate-800 border border-slate-700 p-4 rounded-sm">
                      <h4 className="font-semibold text-white mb-1">{useCase.title}</h4>
                      <p className="text-sm text-slate-400">{useCase.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4 - SIGFOX */}
        <section id="sigfox" className="py-20 md:py-32 bg-white" data-testid="sigfox-section">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div className="order-2 lg:order-1">
                <h3 className="text-xl font-semibold text-slate-900 mb-4">{t('connectivityPage.sigfoxWhen')}</h3>
                <p className="text-slate-600 mb-6">
                  {t('connectivityPage.sigfoxWhenDesc')}
                </p>
                
                <h3 className="text-xl font-semibold text-slate-900 mb-4">{t('connectivityPage.sigfoxUseCases')}</h3>
                <div className="space-y-3">
                  {[
                    { title: t('connectivityPage.sigfoxUse1Title'), desc: t('connectivityPage.sigfoxUse1Desc') },
                    { title: t('connectivityPage.sigfoxUse2Title'), desc: t('connectivityPage.sigfoxUse2Desc') },
                    { title: t('connectivityPage.sigfoxUse3Title'), desc: t('connectivityPage.sigfoxUse3Desc') },
                    { title: t('connectivityPage.sigfoxUse4Title'), desc: t('connectivityPage.sigfoxUse4Desc') },
                  ].map((useCase, index) => (
                    <div key={index} className="bg-slate-50 border border-slate-200 p-4 rounded-sm">
                      <h4 className="font-semibold text-slate-900 mb-1">{useCase.title}</h4>
                      <p className="text-sm text-slate-600">{useCase.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="order-1 lg:order-2">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 rounded-sm bg-orange-500 flex items-center justify-center">
                    <Signal className="h-6 w-6 text-white" />
                  </div>
                  <p className="section-label">{t('connectivityPage.sigfoxBadge')}</p>
                </div>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('connectivityPage.sigfoxTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                  {t('connectivityPage.sigfoxDesc')}
                </p>
                
                <h3 className="text-xl font-semibold text-slate-900 mb-4">{t('connectivityPage.sigfoxAdvantages')}</h3>
                <div className="space-y-3">
                  {[
                    { icon: Battery, text: t('connectivityPage.sigfoxAdv1') },
                    { icon: Zap, text: t('connectivityPage.sigfoxAdv2') },
                    { icon: Target, text: t('connectivityPage.sigfoxAdv3') },
                    { icon: Globe, text: t('connectivityPage.sigfoxAdv4') },
                  ].map((advantage, index) => {
                    const Icon = advantage.icon;
                    return (
                      <div key={index} className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-sm bg-orange-100 flex items-center justify-center">
                          <Icon className="h-5 w-5 text-orange-600" />
                        </div>
                        <span className="text-slate-700 font-medium">{advantage.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 5 - NB-IOT */}
        <section id="nbiot" className="py-20 md:py-32 bg-slate-50" data-testid="nbiot-section">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 rounded-sm bg-blue-600 flex items-center justify-center">
                    <Wifi className="h-6 w-6 text-white" />
                  </div>
                  <p className="section-label">{t('connectivityPage.nbiotBadge')}</p>
                </div>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('connectivityPage.nbiotTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                  {t('connectivityPage.nbiotDesc')}
                </p>
                
                <h3 className="text-xl font-semibold text-slate-900 mb-4">{t('connectivityPage.nbiotAdvantages')}</h3>
                <div className="space-y-3 mb-8">
                  {[
                    { icon: Building2, text: t('connectivityPage.nbiotAdv1') },
                    { icon: Layers, text: t('connectivityPage.nbiotAdv2') },
                    { icon: Shield, text: t('connectivityPage.nbiotAdv3') },
                    { icon: Globe, text: t('connectivityPage.nbiotAdv4') },
                  ].map((advantage, index) => {
                    const Icon = advantage.icon;
                    return (
                      <div key={index} className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-sm bg-blue-100 flex items-center justify-center">
                          <Icon className="h-5 w-5 text-blue-600" />
                        </div>
                        <span className="text-slate-700 font-medium">{advantage.text}</span>
                      </div>
                    );
                  })}
                </div>
                
                <p className="text-slate-600">
                  {t('connectivityPage.nbiotNote')}
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-4">{t('connectivityPage.nbiotUseCases')}</h3>
                <div className="space-y-3">
                  {[
                    { title: t('connectivityPage.nbiotUse1Title'), desc: t('connectivityPage.nbiotUse1Desc') },
                    { title: t('connectivityPage.nbiotUse2Title'), desc: t('connectivityPage.nbiotUse2Desc') },
                    { title: t('connectivityPage.nbiotUse3Title'), desc: t('connectivityPage.nbiotUse3Desc') },
                    { title: t('connectivityPage.nbiotUse4Title'), desc: t('connectivityPage.nbiotUse4Desc') },
                  ].map((useCase, index) => (
                    <div key={index} className="bg-white border border-slate-200 p-4 rounded-sm shadow-sm">
                      <h4 className="font-semibold text-slate-900 mb-1">{useCase.title}</h4>
                      <p className="text-sm text-slate-600">{useCase.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 6 - BLE */}
        <section id="ble" className="py-20 md:py-32 bg-white" data-testid="ble-section">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div className="order-2 lg:order-1">
                <h3 className="text-xl font-semibold text-slate-900 mb-4">{t('connectivityPage.bleUseCases')}</h3>
                <div className="space-y-3">
                  {[
                    { title: t('connectivityPage.bleUse1Title'), desc: t('connectivityPage.bleUse1Desc') },
                    { title: t('connectivityPage.bleUse2Title'), desc: t('connectivityPage.bleUse2Desc') },
                    { title: t('connectivityPage.bleUse3Title'), desc: t('connectivityPage.bleUse3Desc') },
                    { title: t('connectivityPage.bleUse4Title'), desc: t('connectivityPage.bleUse4Desc') },
                  ].map((useCase, index) => (
                    <div key={index} className="bg-slate-50 border border-slate-200 p-4 rounded-sm">
                      <h4 className="font-semibold text-slate-900 mb-1">{useCase.title}</h4>
                      <p className="text-sm text-slate-600">{useCase.desc}</p>
                    </div>
                  ))}
                </div>
                
                <p className="text-slate-600 mt-6">
                  {t('connectivityPage.bleLearnMore')}
                </p>
              </div>
              
              <div className="order-1 lg:order-2">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 rounded-sm bg-violet-600 flex items-center justify-center">
                    <Bluetooth className="h-6 w-6 text-white" />
                  </div>
                  <p className="section-label">{t('connectivityPage.bleBadge')}</p>
                </div>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('connectivityPage.bleTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                  {t('connectivityPage.bleDesc')}
                </p>
                
                <h3 className="text-xl font-semibold text-slate-900 mb-4">{t('connectivityPage.bleAdvantages')}</h3>
                <div className="space-y-3">
                  {[
                    t('connectivityPage.bleAdv1'),
                    t('connectivityPage.bleAdv2'),
                    t('connectivityPage.bleAdv3'),
                    t('connectivityPage.bleAdv4'),
                  ].map((advantage, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <CheckCircle className="h-5 w-5 text-violet-600 flex-shrink-0" />
                      <span className="text-slate-700">{advantage}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 7 - SATELLITE */}
        <section id="satellite" className="py-20 md:py-32 bg-slate-900" data-testid="satellite-section">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <div className="w-12 h-12 rounded-sm bg-teal-600 flex items-center justify-center">
                  <Satellite className="h-6 w-6 text-white" />
                </div>
              </div>
              <p className="text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">{t('connectivityPage.satelliteBadge')}</p>
              <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                {t('connectivityPage.satelliteTitle')}
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed">
                {t('connectivityPage.satelliteDesc')}
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {[
                { title: t('connectivityPage.satelliteAdv1Title'), desc: t('connectivityPage.satelliteAdv1Desc') },
                { title: t('connectivityPage.satelliteAdv2Title'), desc: t('connectivityPage.satelliteAdv2Desc') },
                { title: t('connectivityPage.satelliteAdv3Title'), desc: t('connectivityPage.satelliteAdv3Desc') },
              ].map((advantage, index) => (
                <div key={index} className="bg-slate-800 border border-slate-700 p-6 rounded-sm text-center">
                  <h3 className="text-lg font-semibold text-white mb-2">{advantage.title}</h3>
                  <p className="text-sm text-slate-400">{advantage.desc}</p>
                </div>
              ))}
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-slate-800 border border-slate-700 p-4 rounded-sm">
                <h4 className="font-semibold text-white mb-2">{t('connectivityPage.satelliteUse1Title')}</h4>
                <p className="text-sm text-slate-400">{t('connectivityPage.satelliteUse1Desc')}</p>
              </div>
              <div className="bg-slate-800 border border-slate-700 p-4 rounded-sm">
                <h4 className="font-semibold text-white mb-2">{t('connectivityPage.satelliteUse2Title')}</h4>
                <p className="text-sm text-slate-400">{t('connectivityPage.satelliteUse2Desc')}</p>
              </div>
              <div className="bg-slate-800 border border-slate-700 p-4 rounded-sm">
                <h4 className="font-semibold text-white mb-2">{t('connectivityPage.satelliteUse3Title')}</h4>
                <p className="text-sm text-slate-400">{t('connectivityPage.satelliteUse3Desc')}</p>
              </div>
            </div>
            
            <p className="text-center text-slate-400 mt-8 text-sm">
              {t('connectivityPage.satelliteNote')}
            </p>
          </div>
        </section>

        {/* SECTION 8 - CONNECTIVITY SELECTION STRATEGY */}
        <section className="py-20 md:py-32 bg-white" data-testid="strategy-section">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto">
              <p className="section-label text-center">{t('connectivityPage.strategyBadge')}</p>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6 text-center">
                {t('connectivityPage.strategyTitle')}
              </h2>
              <p className="text-lg text-slate-600 mb-12 text-center leading-relaxed">
                {t('connectivityPage.strategyDesc')}
              </p>
              
              <div className="grid md:grid-cols-5 gap-4 mb-12">
                {[
                  { step: t('connectivityPage.step1'), title: t('connectivityPage.step1Title'), desc: t('connectivityPage.step1Desc') },
                  { step: t('connectivityPage.step2'), title: t('connectivityPage.step2Title'), desc: t('connectivityPage.step2Desc') },
                  { step: t('connectivityPage.step3'), title: t('connectivityPage.step3Title'), desc: t('connectivityPage.step3Desc') },
                  { step: t('connectivityPage.step4'), title: t('connectivityPage.step4Title'), desc: t('connectivityPage.step4Desc') },
                  { step: t('connectivityPage.step5'), title: t('connectivityPage.step5Title'), desc: t('connectivityPage.step5Desc') },
                ].map((item, index) => (
                  <div key={index} className="text-center">
                    <div className="w-12 h-12 rounded-full bg-teal-600 text-white font-bold flex items-center justify-center mx-auto mb-3">
                      {item.step}
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-2 text-sm">{item.title}</h3>
                    <p className="text-xs text-slate-600">{item.desc}</p>
                  </div>
                ))}
              </div>
              
              <div className="bg-teal-50 border border-teal-200 p-6 rounded-sm text-center">
                <p className="text-teal-800 font-medium">
                  {t('connectivityPage.strategyNote')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 9 - INDUSTRY APPLICATIONS */}
        <section className="py-20 md:py-32 bg-slate-50" data-testid="industries-section">
          <div className="container-0g">
            <p className="section-label text-center">{t('connectivityPage.industriesBadge')}</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6 text-center">
              {t('connectivityPage.industriesTitle')}
            </h2>
            <p className="text-lg text-slate-600 mb-12 text-center max-w-2xl mx-auto">
              {t('connectivityPage.industriesDesc')}
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {[
                { icon: Building2, title: t('connectivityPage.industrySmartCities'), link: '/#solutions' },
                { icon: Droplets, title: t('connectivityPage.industryUtilities'), link: '/#solutions' },
                { icon: Truck, title: t('connectivityPage.industryLogistics'), link: '/#partners' },
                { icon: Heart, title: t('connectivityPage.industryHealthcare'), link: '/#partners' },
                { icon: Home, title: t('connectivityPage.industryRealEstate'), link: '/#solutions' },
                { icon: Factory, title: t('connectivityPage.industryIndustrial'), link: '/#solutions' },
              ].map((industry, index) => {
                const Icon = industry.icon;
                return (
                  <Link
                    key={index}
                    to={industry.link}
                    className="group bg-white border border-slate-200 p-6 rounded-sm text-center hover:border-teal-500 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-sm bg-slate-100 flex items-center justify-center mx-auto mb-3 group-hover:bg-teal-600 transition-colors">
                      <Icon className="h-6 w-6 text-slate-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-semibold text-slate-900 text-sm">{industry.title}</h3>
                  </Link>
                );
              })}
            </div>
            
            <div className="mt-12 text-center">
              <p className="text-slate-600 mb-4">
                {t('connectivityPage.industriesExplore')}
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 10 - CTA */}
        <section className="py-20 md:py-32 cta-gradient" data-testid="connectivity-cta">
          <div className="container-0g">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                {t('connectivityPage.ctaTitle')}
              </h2>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                {t('connectivityPage.ctaDesc')}
              </p>
              <Button 
                className="btn-primary bg-teal-500 hover:bg-teal-400 text-white px-8 py-4 text-lg inline-flex items-center gap-2"
                onClick={scrollToContact}
                data-testid="connectivity-cta-button"
              >
                <Calendar className="h-5 w-5" />
                {t('connectivityPage.ctaButton')}
              </Button>
            </div>
          </div>
        </section>

        {/* CONTACT FORM */}
        <ContactForm />
      </main>

      <Footer />
    </>
  );
};

export default ConnectivityPage;
